import React, { useState } from 'react';
import {
  Modal,
  Box,
  Typography,
  TextField,
  Button,
  List,
  ListItem,
  ListItemIcon,
  ListItemText
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';

type Props = {
  open: boolean;
  onClose: () => void;
  onSubmit: (newPassword: string) => void;
  email: string;
};

const validatePassword = (pw: string) => {
  return {
    length: pw.length >= 8,
    uppercase: /[A-Z]/.test(pw),
    lowercase: /[a-z]/.test(pw),
    number: /\d/.test(pw),
    special: /[^A-Za-z0-9]/.test(pw)
  };
};

const NewPasswordModal: React.FC<Props> = ({ open, onClose, onSubmit, email }) => {
  const [newPassword, setNewPassword] = useState<string>('');
  const [error, setError] = useState<string>('');
  const policy = validatePassword(newPassword);

  const allValid = Object.values(policy).every(Boolean);

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!allValid) {
      setError('Password does not meet all requirements.');
      return;
    }

    setError('');
    onSubmit(newPassword);
  };

  const renderRequirement = (label: string, met: boolean) => (
    <ListItem>
      <ListItemIcon>
        {met ? (
          <CheckCircleIcon color="success" />
        ) : (
          <CancelIcon color="error" />
        )}
      </ListItemIcon>
      <ListItemText primary={label} />
    </ListItem>
  );

  return (
    <Modal open={open} onClose={onClose}>
      <Box
        sx={{
          position: 'absolute' as const,
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          bgcolor: 'background.paper',
          boxShadow: 24,
          p: 4,
          width: 450,
          borderRadius: 2
        }}
      >
        <Typography component="h1" variant="h5" gutterBottom>
          Set New Password
        </Typography>

        <Typography variant="body2" gutterBottom>
          Hello {email}, please choose a new password to continue.
        </Typography>

        <Box component="form" onSubmit={handleSubmit} sx={{ mt: 2 }}>
          <TextField
            fullWidth
            required
            margin="normal"
            label="New Password"
            type="password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
          />

          {error && (
            <Typography variant="subtitle2" color="error.main" sx={{ mt: 1 }}>
              {error}
            </Typography>
          )}

          <Typography variant="subtitle2" sx={{ mt: 2, fontWeight: 600 }}>
            Password must contain:
          </Typography>

          <List dense>
            {renderRequirement('At least 8 characters', policy.length)}
            {renderRequirement('At least one uppercase letter (A-Z)', policy.uppercase)}
            {renderRequirement('At least one lowercase letter (a-z)', policy.lowercase)}
            {renderRequirement('At least one number (0-9)', policy.number)}
            {renderRequirement('At least one special character (!@#$%^&*)', policy.special)}
          </List>

          <Button
            type="submit"
            variant="contained"
            fullWidth
            sx={{ mt: 3 }}
            disabled={!newPassword}
          >
            Submit New Password
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

export default NewPasswordModal;
